"use client";

import { useEffect, useRef, useState } from "react";
import "./customer.css"

type IconName =
  | "building"
  | "users"
  | "globe"
  | "trophy"
  | "chart"
  | "star"
  | "target"
  | "layers"
  | "clock"
  | "handshake"
  | "search"
  | "arrow"
  | "play"
  | "chevron";




const heroImage = "/assets/images/herocustomer.jpg";

const storyImages = [
  "/assets/images/haldiram.jpg",
  "/assets/images/zydus-wellness.jpg",
  "/assets/images/ds-group.jpg",
  "/assets/images/ethiopian.jpg",
];

// Reveals an element with a fade + gentle upward drift the first time it
// scrolls into view. No left/right movement — only opacity + translateY.
function useReveal<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, visible };
}

function Icon({ name, size = 26 }: { name: IconName; size?: number }) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };

  if (name === "building")
    return (
      <svg {...common}>
        <path d="M4 21V5a2 2 0 0 1 2-2h9v18" />
        <path d="M15 9h3a2 2 0 0 1 2 2v10" />
        <path d="M8 7h3M8 11h3M8 15h3M8 19h3M18 14h1M18 18h1" />
      </svg>
    );

  if (name === "users")
    return (
      <svg {...common}>
        <circle cx="9" cy="8" r="3" />
        <path d="M3 20a6 6 0 0 1 12 0" />
        <circle cx="17" cy="9" r="2.4" />
        <path d="M16 14.5a5 5 0 0 1 5 5.5" />
      </svg>
    );

  if (name === "globe")
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3c3 3.2 3 14.8 0 18M12 3c-3 3.2-3 14.8 0 18" />
      </svg>
    );

  if (name === "trophy")
    return (
      <svg {...common}>
        <path d="M8 4h8v4a4 4 0 0 1-8 0V4Z" />
        <path d="M8 6H4v1a4 4 0 0 0 4 4M16 6h4v1a4 4 0 0 1-4 4M12 12v5M8 21h8M9 17h6" />
      </svg>
    );

  if (name === "chart")
    return (
      <svg {...common}>
        <path d="M4 19V5M4 19h17" />
        <path d="m7 15 4-4 3 2 5-7" />
        <path d="M16 6h3v3" />
      </svg>
    );

  if (name === "star")
    return (
      <svg {...common}>
        <path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-3-5.6 3 1.1-6.2L3 9.6l6.2-.9L12 3Z" />
      </svg>
    );

  if (name === "target")
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="8.5" />
        <circle cx="12" cy="12" r="5" />
        <circle cx="12" cy="12" r="1.8" />
        <path d="m16 8 4-4M17 4h3v3" />
      </svg>
    );

  if (name === "layers")
    return (
      <svg {...common}>
        <path d="m12 3 9 5-9 5-9-5 9-5Z" />
        <path d="m3 12 9 5 9-5M3 16l9 5 9-5" />
      </svg>
    );

  if (name === "clock")
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </svg>
    );

  if (name === "handshake")
    return (
      <svg {...common}>
        <path d="m3 10 4-4 5 3 3-2 6 5-3 3-4-2-3 3-8-6Z" />
        <path d="m7 14 3 3M10 12l3 3M14 10l3 2" />
      </svg>
    );

  if (name === "search")
    return (
      <svg {...common}>
        <circle cx="10.5" cy="10.5" r="6.5" />
        <path d="m16 16 5 5" />
      </svg>
    );

  if (name === "play")
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="9" />
        <path d="m10 8 5 4-5 4V8Z" fill="currentColor" stroke="none" />
      </svg>
    );

  if (name === "chevron")
    return (
      <svg {...common}>
        <path d="m7 10 5 5 5-5" />
      </svg>
    );

  return (
    <svg {...common}>
      <path d="M5 12h13M13 7l5 5-5 5" />
    </svg>
  );
}

const stats = [
  ["building", "200+", "Customers"],
  ["users", "300+", "Projects"],
  ["globe", "6", "Continents"],
  ["trophy", "95%", "Repeat Business"],
  ["chart", "20–40%", "Typical Measurable Impact"],
  ["star", "Long-Term", "Partnerships"],
] as const;


const logos = [
  { name: "Haldiram's", img: "/assets/images/haldirams.png" },
  { name: "Zydus Wellness", img: "/assets/images/zydus-wellness.png" },
  { name: "DS Group", img: "/assets/images/ds-group.png" },
  { name: "Ethiopian Airlines", img: "/assets/images/ethiopian.png" },
  { name: "Dashen Bank", img: "/assets/images/dashen-bank.png" },
  { name: "ECG", img: "/assets/images/ecg.png" },
  { name: "Jindal", img: "/assets/images/jindal.png" },
  { name: "L&T", img: "/assets/images/lt.png" },
];

const filters = [
  "All",
  "FMCG & Retail",
  "Energy & Utilities",
  "Manufacturing",
  "Pharma & Life Sciences",
  "BFSI",
  "Travel & Airlines",
  "Professional Services",
  "Public Sector",
  "Others",
];

const stories = [
  {
    image: storyImages[0],
    category: "FMCG & Retail",
    title: "Integrated Finance, Supply Chain and Retail Operations",
    text: "Supporting India and Nepal operations with SAP, planning, pricing and managed services.",
    metrics: [
      ["20%", "Faster closing"],
      ["15%", "Lower inventory"],
    ],
  },
  {
    image: storyImages[1],
    category: "Pharma & Life Sciences",
    title: "AI-Enabled Sales & COGS Planning Platform",
    text: "Built a planning platform with real-time analytics, S&OP and AI-driven forecasting.",
    metrics: [["30%", "Better forecast accuracy"]],
  },
  {
    image: storyImages[2],
    category: "FMCG & Retail",
    title: "S/4HANA Migration with 9 Verticals and 5-Year AMS",
    text: "End-to-end transformation with RISE, Signavio, OpenText and long-term managed services.",
    metrics: [["Higher", "Process efficiency"]],
  },
  {
    image: storyImages[3],
    category: "Airlines & Travel",
    title: "Digital Transformation under Vision 2025",
    text: "Program leadership for multiple systems including ERP, planning and operational transformation.",
    metrics: [["Improved", "Network profitability"]],
  },
];

const differentiators = [
  ["target", "Deep Business", "Understanding"],
  ["users", "Experienced", "People & Teams"],
  ["layers", "SAP + AI", "Expertise"],
  ["globe", "Global Delivery", "with Local Context"],
  ["chart", "Measurable", "Business Impact"],
  ["handshake", "Long-Term", "Partnerships"],
] as const;

const footerGroups = [
  {
    title: "What We Do",
    links: ["Enterprise Transformation", "Data & SAP BDC", "AI & Intelligence", "Operate & Improve"],
  },
  {
    title: "Knowledge",
    links: ["Business Problems (S2)", "Frameworks & Templates", "Case Studies", "Research & Perspectives"],
  },
  {
    title: "Industries",
    links: [
      "FMCG & Retail",
      "Energy & Utilities",
      "Manufacturing",
      "Pharma & Life Sciences",
      "Hotels & Hospitality",
      "BFSI",
      "Airlines & Airports",
      "Professional Services",
    ],
  },
  {
    title: "Company",
    links: ["About Orane", "Careers", "Contact", "Partnerships", "Leadership"],
  },
];

export default function Customers() {
  const stats$ = useReveal<HTMLDivElement>();
  const logos$ = useReveal<HTMLDivElement>();
  const stories$ = useReveal<HTMLDivElement>();
  const lower$ = useReveal<HTMLDivElement>();
  const impact$ = useReveal<HTMLDivElement>();
  const cta$ = useReveal<HTMLDivElement>();

  return (
    <main className="customerPage">
<section className="customerHero">
        <img className="customerHeroImage" src={heroImage} alt="" />
        <div className="customerHeroOverlay" />
        <div className="customerHeroContent customerLoadIn">
          <h1 className="customerLoadIn" style={{ animationDelay: "0.1s" }}>Real Customers.<br /><span>Real Outcomes.</span></h1>
          <p className="customerHeroText customerLoadIn" style={{ animationDelay: "0.25s" }}>
            From complex challenges to measurable business impact.<br />
            We bring together business understanding, SAP and AI<br className="customerDesktopOnly" />
            to help enterprises perform, grow and transform.
          </p>
          <div className="customerHeroButtons customerLoadIn" style={{ animationDelay: "0.4s" }}>
            <a href="customers/Customer-Stories" className="fp-buttons fp-btn-primary customerPrimaryBtn">Explore Customer Stories <Icon name="arrow" size={17} /></a>
            <a href="#" className="customerSecondaryBtn"><Icon name="play" size={18} /> Watch Client Video (2 min)</a>
          </div>
        </div>
       
      </section>

      <section
        className={`customerStatsBar customerReveal${stats$.visible ? " customerInView" : ""}`}
        ref={stats$.ref}
      >
        {stats.map(([icon, value, label], i) => (
          <div
            className="customerStat"
            key={label}
            style={{ transitionDelay: stats$.visible ? `${i * 0.06}s` : "0s" }}
          >
            <Icon name={icon} size={32} />
            <div><strong>{value}</strong><span>{label}</span></div>
          </div>
        ))}
      </section>

      <section className="customerContainer">
        <div
          className={`customerLogoStrip customerReveal${logos$.visible ? " customerInView" : ""}`}
          ref={logos$.ref}
        >
  {logos.map((logo) => (
    <div className="customerClientLogo" key={logo.name}>
      <img src={logo.img} alt={logo.name} />
    </div>
  ))}
</div>
        <a className="customerViewAll" href="/customers/Customer-Stories">View All Customers <Icon name="arrow" size={14} /></a>

        <div className="customerFilterRow">
          {filters.map((filter, index) => (
            <button className={index === 0 ? "customerSelected" : ""} key={filter}>{filter}</button>
          ))}
        </div>

        <div className="customerSectionHeading">
          <h2>Featured Customer Stories</h2>
          <a href="customers/Customer-Stories">View All Case Studies <Icon name="arrow" size={15} /></a>
        </div>

        <div
          className={`customerStoryGrid customerReveal${stories$.visible ? " customerInView" : ""}`}
          ref={stories$.ref}
        >
          {stories.map((story, i) => (
            <article
              className="customerStoryCard"
              key={story.title}
              style={{ transitionDelay: stories$.visible ? `${i * 0.08}s` : "0s" }}
            >
              <img src={story.image} alt="" />
              <div className="customerStoryBody">
                <span className="customerCategory">{story.category}</span>
                <h3>{story.title}</h3>
                <p>{story.text}</p>
                <div className="customerStoryMetrics">
                  {story.metrics.map(([value, label]) => (
                    <div key={label}>
                      <strong>{value}</strong>
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
                <a className="customerReadMore" href="customers/Customer-Stories">Read the case study <Icon name="arrow" size={15} /></a>
              </div>
            </article>
          ))}
        </div>

        <div
          className={`customerLowerGrid customerReveal${lower$.visible ? " customerInView" : ""}`}
          ref={lower$.ref}
        >
          <div className="customerChooseBox">
            <div className="customerBoxHeading">
              <h2>Why Customers Choose Orane</h2>
              <a href="/about">Our Differentiators <Icon name="arrow" size={14} /></a>
            </div>
            <div className="customerDiffGrid">
              {differentiators.map(([icon, line1, line2]) => (
                <div className="customerDiffItem" key={line1}>
                  <Icon name={icon} size={34} />
                  <span>{line1}<br />{line2}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <section
          className={`customerImpact customerReveal${impact$.visible ? " customerInView" : ""}`}
          ref={impact$.ref}
        >
          <h2>Customer Impact Across Key Areas</h2>
          <div className="customerImpactRow">
            {[
              ["chart", "20–30%", "Revenue improvement"],
              ["chart", "15–25%", "Cost optimisation"],
              ["target", "20–40%", "Inventory reduction"],
              ["clock", "30–50%", "Process cycle time improvement"],
              ["users", "Higher", "Customer satisfaction"],
            ].map(([icon, value, label], i) => (
              <div
                className="customerImpactItem"
                key={label}
                style={{ transitionDelay: impact$.visible ? `${i * 0.07}s` : "0s" }}
              >
                <Icon name={icon as IconName} size={33} />
                <div><strong>{value}</strong><span>{label}</span></div>
              </div>
            ))}
          </div>
        </section>
      </section>

      <section
        className={`customerCtaBanner customerReveal${cta$.visible ? " customerInView" : ""}`}
        ref={cta$.ref}
      >
        <img src={heroImage} alt="" />
        <div className="customerCtaShade" />
        <div className="customerCtaText">
          <p>READY TO CREATE YOUR SUCCESS STORY?</p>
          <h2>Let’s solve your business problem.</h2>
          <span>Talk to our experts and explore how we can create real value together.</span>
          <div className="customerCtaActions">
            <a className="fp-buttons fp-btn-primary customerCtaButton" href="tellusyourproblem#tell-details">
              Tell Us Your Problem <Icon name="arrow" size={17} />
            </a>
          </div>
        </div>
      </section>
</main>
  );
}